// ParentRequestDetailPopup.tsx - Shows parent request details in a modal popup
import React, { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface Region {
  regionId: number;
  regionName: string;
  regionCode: string;
  description?: string;
  isActive: boolean;
}

interface ParentRequestDetailPopupProps {
  isOpen: boolean;
  parentRequestId: number;
  getAccessToken: () => Promise<string>;
  onClose: () => void;
  regions?: Region[];
}

export const ParentRequestDetailPopup: React.FC<ParentRequestDetailPopupProps> = ({
  isOpen,
  parentRequestId,
  getAccessToken,
  onClose,
  regions = []
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parentData, setParentData] = useState<any>(null);

  // Lock background scroll when popup is open
  useEffect(() => {
    if (!isOpen) return;

    // Prevent scroll on all scrollable ancestors
    const handleWheel = (e: WheelEvent) => {
      const popup = document.getElementById('parent-request-popup-body');
      if (popup && popup.contains(e.target as Node)) {
        // Allow scrolling inside the popup body
        return;
      }
      // Block scrolling everywhere else
      e.preventDefault();
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchmove', handleWheel as any, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleWheel as any);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !parentRequestId) return;

    let cancelled = false;
    const fetchParentDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const url = `${API_BASE_URL}/gatepass/${parentRequestId}`;
        console.log('🔍 Fetching parent request details:', url);

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const responseText = await response.text();
        let data: any;
        try {
          data = JSON.parse(responseText);
        } catch {
          throw new Error('Invalid response from server');
        }

        if (!response.ok || !data.success) {
          throw new Error(data.error || data.message || 'Failed to load parent request');
        }

        if (!cancelled) {
          console.log('✅ Parent request loaded:', data.data?.id);
          setParentData(data.data);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('❌ Failed to fetch parent request:', err);
          setError(err.message || 'Failed to load parent request details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchParentDetails();
    return () => { cancelled = true; };
  }, [isOpen, parentRequestId, getAccessToken]);

  // Lock background scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = originalOverflow; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ─── Helpers ──────────────────────────────────────────────
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  const formatPassType = (pt: string): string => {
    switch (pt) {
      case 'single_visit': return 'Single Visit';
      case 'short_term': return 'Short Term';
      case 'long_term': return 'Long Term';
      default: return pt || '—';
    }
  };

  const formatRequestType = (rt: string): string => {
    switch (rt) {
      case 'new': return 'New';
      case 'supplementary': return 'Supplementary';
      default: return rt || '—';
    }
  };

  const getRegionName = (regionId: number | null): string => {
    if (!regionId) return '—';
    const region = regions.find(r => r.regionId === regionId);
    return region ? region.regionName : `Region ${regionId}`;
  };

  const getStatusDisplay = (state: string): { label: string; color: string; bg: string } => {
    const map: { [key: string]: { label: string; color: string; bg: string } } = {
      'DRAFT': { label: 'Draft', color: '#6c757d', bg: '#f1f3f5' },
      'SUBMITTED': { label: 'Submitted', color: '#0d6efd', bg: '#e7f1ff' },
      'COMPANY_APPROVER_PENDING': { label: 'Company Approval', color: '#e67700', bg: '#fff3bf' },
      'COMPANY_REVIEWER_PENDING': { label: 'Company Review', color: '#d9480f', bg: '#ffe8cc' },
      'GATE_REVIEWER_PENDING': { label: 'Gate Review', color: '#7048e8', bg: '#f3f0ff' },
      'GATE_APPROVER_PENDING': { label: 'Gate Approval', color: '#1098ad', bg: '#e3fafc' },
      'SECURITY_APPROVER_PENDING': { label: 'Security Approval', color: '#0d6efd', bg: '#e7f1ff' },
      'PARTIALLY_APPROVED': { label: 'Partially Approved', color: '#0ca678', bg: '#e6fcf5' },
      'FULLY_APPROVED': { label: 'Approved', color: '#2b8a3e', bg: '#ebfbee' },
      'REJECTED': { label: 'Rejected', color: '#e03131', bg: '#fff5f5' },
      'RETURNED': { label: 'Returned', color: '#c2255c', bg: '#fff0f6' },
      'CANCELLED': { label: 'Cancelled', color: '#6c757d', bg: '#f1f3f5' },
      'EXPIRED': { label: 'Expired', color: '#495057', bg: '#f1f3f5' }
    };
    return map[state] || { label: state, color: '#6c757d', bg: '#f1f3f5' };
  };

  const calculateDuration = (start: string, end: string): string => {
    if (!start || !end) return '—';
    const s = new Date(start), e = new Date(end);
    const days = Math.ceil(Math.abs(e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  // ─── Styles ───────────────────────────────────────────────
  const labelStyle: React.CSSProperties = {
    fontSize: '12px', color: '#6b7280', fontWeight: 500, marginBottom: '2px'
  };
  const valueStyle: React.CSSProperties = {
    fontSize: '14px', color: '#1e293b', fontWeight: 500
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
        overflow: 'hidden'
      }}
    >
      <div
        onWheel={(e) => e.stopPropagation()}
        style={{
        background: '#ffffff',
        borderRadius: '4px',
        width: '100%',
        maxWidth: '720px',
        maxHeight: '85vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Segoe UI', sans-serif"
      }}>

        {/* ─── Header ─────────────────────────────────────────── */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '8px',
              backgroundColor: '#FEF6F4',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#242424' }}>
                Parent Request #{parentRequestId}
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                Original request details
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px', borderRadius: '6px', color: '#6b7280',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ─── Body ───────────────────────────────────────────── */}
        <div id="parent-request-popup-body" style={{ padding: '24px', overflowY: 'auto', flex: 1, overscrollBehavior: 'contain' }}>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{
                width: '32px', height: '32px',
                border: '3px solid #e2e8f0', borderTopColor: '#F3694A',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                margin: '0 auto 16px'
              }} />
              <div style={{ color: '#64748b', fontSize: '14px' }}>Loading parent request details...</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              textAlign: 'center', padding: '48px 24px',
              backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
              <div style={{ color: '#991b1b', fontSize: '14px', fontWeight: 500 }}>{error}</div>
            </div>
          )}

          {/* Content */}
          {!loading && !error && parentData && (() => {
            const status = getStatusDisplay(parentData.workflow?.currentState || 'FULLY_APPROVED');
            const gates = parentData.gates || [];
            const personnel = parentData.personnel || [];
            const activeGates = gates.filter((g: any) => !g.isExcluded);
            const activePersonnel = personnel.filter((p: any) => !p.isExcluded);

            return (
              <>
                {/* Status Bar */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  marginBottom: '24px', padding: '14px 16px',
                  backgroundColor: '#f8fafc', borderRadius: '4px',
                  border: '1px solid #e2e8f0'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#F3694A' }}>
                    #{parentData.id}
                  </span>
                  <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>
                    {parentData.name}
                  </span>
                  <span style={{
                    fontSize: '11px', fontWeight: 600,
                    color: status.color, backgroundColor: status.bg,
                    padding: '2px 10px', borderRadius: '4px'
                  }}>
                    {status.label}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#64748b' }}>
                    {parentData.companyName}
                  </span>
                </div>

                {/* Details Grid */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr',
                  gap: '16px 32px', marginBottom: '24px'
                }}>
                  <div>
                    <div style={labelStyle}>Requester</div>
                    <div style={valueStyle}>{parentData.name}</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Email</div>
                    <div style={valueStyle}>{parentData.email}</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Company</div>
                    <div style={valueStyle}>{parentData.companyName}</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Region</div>
                    <div style={valueStyle}>{getRegionName(parentData.regionId)}</div>
                  </div>
                  {parentData.requestType && (
                    <div>
                      <div style={labelStyle}>Request Type</div>
                      <div style={valueStyle}>{formatRequestType(parentData.requestType)}</div>
                    </div>
                  )}
                  {parentData.passType && (
                    <div>
                      <div style={labelStyle}>Pass Type</div>
                      <div style={valueStyle}>{formatPassType(parentData.passType)}</div>
                    </div>
                  )}
                  {parentData.startDate && (
                    <div>
                      <div style={labelStyle}>Start Date</div>
                      <div style={valueStyle}>{formatDate(parentData.startDate)}</div>
                    </div>
                  )}
                  {parentData.endDate && (
                    <div>
                      <div style={labelStyle}>End Date</div>
                      <div style={valueStyle}>{formatDate(parentData.endDate)}</div>
                    </div>
                  )}
                  {parentData.startDate && parentData.endDate && (
                    <div>
                      <div style={labelStyle}>Duration</div>
                      <div style={valueStyle}>{calculateDuration(parentData.startDate, parentData.endDate)}</div>
                    </div>
                  )}
                  {parentData.contractNumber && (
                    <div>
                      <div style={labelStyle}>Contract Number</div>
                      <div style={valueStyle}>{parentData.contractNumber}</div>
                    </div>
                  )}
                  {parentData.passIdentificationName && (
                    <div>
                      <div style={labelStyle}>Pass ID Name</div>
                      <div style={valueStyle}>{parentData.passIdentificationName}</div>
                    </div>
                  )}
                </div>

                {/* Gates */}
                {activeGates.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
                      Gates ({activeGates.length})
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>Gate</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>Description</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>Access Codes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeGates.map((gate: any) => (
                          <tr key={gate.gateId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>{gate.gateNumber}</td>
                            <td style={{ padding: '8px 12px', color: '#475569' }}>{gate.description || '—'}</td>
                            <td style={{ padding: '8px 12px', color: '#475569' }}>{gate.accessCodes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Personnel */}
                {activePersonnel.length > 0 && (
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
                      Personnel ({activePersonnel.length})
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>Name</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>National ID</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>Nationality</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>Visa Expiry</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: '12px' }}>Passport Expiry</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activePersonnel.map((person: any, idx: number) => {
                          const name = person.fullName || (person.firstName && person.lastName ? `${person.firstName} ${person.lastName}` : null) || person.nationalIdNo;
                          const visaExpired = person.visaExpiry && new Date(person.visaExpiry) < new Date();
                          const passportExpired = person.passportExpiryDate && new Date(person.passportExpiryDate) < new Date();
                          return (
                            <tr key={person.gatePassPersonnelId || person.personnelId || idx}
                                style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>{name}</td>
                              <td style={{ padding: '8px 12px', color: '#475569' }}>{person.nationalIdNo}</td>
                              <td style={{ padding: '8px 12px', color: '#475569' }}>{person.nationality || '—'}</td>
                              <td style={{ padding: '8px 12px' }}>
                                {person.visaExpiry ? (
                                  <span style={{ color: visaExpired ? '#dc2626' : '#475569', fontWeight: visaExpired ? 600 : 400 }}>
                                    {formatDate(person.visaExpiry)}
                                    {visaExpired && <span style={{ marginLeft: '4px', fontSize: '10px', color: '#dc2626' }}>EXPIRED</span>}
                                  </span>
                                ) : '—'}
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                {person.passportExpiryDate ? (
                                  <span style={{ color: passportExpired ? '#dc2626' : '#475569', fontWeight: passportExpired ? 600 : 400 }}>
                                    {formatDate(person.passportExpiryDate)}
                                    {passportExpired && <span style={{ marginLeft: '4px', fontSize: '10px', color: '#dc2626' }}>EXPIRED</span>}
                                  </span>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* ─── Footer ─────────────────────────────────────────── */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#ffffff', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: '4px',
              cursor: 'pointer', fontSize: '14px', fontWeight: 600,
              fontFamily: "'Segoe UI', sans-serif"
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};